import { FormControl, FormControlProps, Stack, TextField, Typography } from '@mui/material'
import { useEffect, useRef, useState, KeyboardEvent, ClipboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { sanitizeShortCodeInput, SHORT_CODE_LENGTH } from '../utils/deviceCode'

enum KeyboardKey {
  BACKSPACE = 'Backspace',
  ARROW_LEFT = 'ArrowLeft',
  ARROW_RIGHT = 'ArrowRight',
  ENTER = 'Enter'
}

export interface CodeInputProps extends Omit<FormControlProps, 'onChange'> {
  /**
   * The handler for the change event.
   */
  onChange?: (value: string) => void
  /**
   * Focus the first input automatically when the component mounts.
   */
  autoFocusInput?: boolean
  /**
   * Optional external value to display in the inputs.
   */
  value?: string

  label?: string

  length?: number
}

export default function CodeInput({
  onChange,
  label,
  length = 6,
  autoFocusInput = true,
  value: externalValue,
  sx,
  ...props
}: CodeInputProps) {
  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  /**
   * The digits.
   */
  const digits = Array.from(Array(Math.round(length)).keys())

  /**
   * The input refs.
   */
  const refs = useRef<HTMLInputElement[]>([])

  /**
   * The input value as an array of digits.
   */
  const [value, setValue] = useState<string[]>(() => digits.map(() => ''))

  /**
   * The consecutive backspace hit counter per input.
   *
   * This is needed so that we can change focus when the backspace is pressed
   * to an input with an empty value as this does not trigger a change event.
   * Moreover, since change event is fired before the keyup event, we would not
   * be able to determine in the keyup handler if the input was already empty or not.
   */
  const [backspaceHits, setBackspaceHits] = useState<number[]>(() => digits.map(() => 0))

  const updateValue = (nextValue: string[]) => {
    setValue(nextValue)
    onChange?.(nextValue.join(''))
  }

  /**
   * Handle the change event.
   */
  const handleChange = (index: number, character: string) => {
    if (character === ' ') {
      return
    }

    if (character === '') {
      const newValue = [...value]
      newValue[index] = ''
      updateValue(newValue)
      return
    }

    const isDigit = /^\d$/.test(character)

    if (!isDigit) {
      return
    }

    const newValue = [...value]
    newValue[index] = character
    updateValue(newValue)

    if (refs.current[index + 1]) {
      refs.current[index + 1].focus()
    }
  }

  /**
   * Handle the key up event.
   */
  const handleKeyUp = (
    index: number,
    event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newBackspaceHits = [...backspaceHits]

    if (event.key === KeyboardKey.BACKSPACE) {
      newBackspaceHits[index]++

      if (newBackspaceHits[index] > 1 && refs.current[index - 1]) {
        refs.current[index - 1].focus()
      }
    } else {
      if (index === backspaceHits.length - 1) {
        newBackspaceHits[index] = 0
      } else {
        newBackspaceHits[index - 1] = 0
      }
    }

    setBackspaceHits(newBackspaceHits)

    switch (event.key) {
      case KeyboardKey.ARROW_LEFT:
        if (refs.current[index - 1]) {
          refs.current[index - 1].focus()
        }
        break
      case KeyboardKey.ARROW_RIGHT:
      case KeyboardKey.ENTER:
        if (refs.current[index + 1]) {
          refs.current[index + 1].focus()
        }
        break
    }
  }

  /**
   * Handle the paste event.
   */
  const handlePaste = (
    index: number,
    event: ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    event.preventDefault()

    const digitsFromClipboard = sanitizeShortCodeInput(event.clipboardData.getData('text'))

    if (digitsFromClipboard.length === 0) {
      return
    }

    const digitsToInsert = digitsFromClipboard.split('')
    const newValue = [...value]

    digitsToInsert.forEach((digit, digitIndex) => {
      const targetIndex = index + digitIndex
      if (targetIndex < refs.current.length) {
        newValue[targetIndex] = digit
      }
    })

    updateValue(newValue)
  }

  /**
   * Build refs when the component mounts.
   */
  useEffect(() => {
    refs.current = refs.current.slice(0, digits.length)
  }, [])

  useEffect(() => {
    if (externalValue === undefined) {
      return
    }

    const sanitized = sanitizeShortCodeInput(externalValue)
    const currentValue = value.join('')

    if (sanitized === currentValue) {
      return
    }

    if (sanitized.length === 0) {
      setValue(Array.from({ length: SHORT_CODE_LENGTH }, () => ''))
      setBackspaceHits(Array.from({ length: SHORT_CODE_LENGTH }, () => 0))
      return
    }

    const nextValue = Array.from(
      { length: SHORT_CODE_LENGTH },
      (_, index) => sanitized[index] ?? ''
    )
    setValue(nextValue)
    setBackspaceHits(Array.from({ length: SHORT_CODE_LENGTH }, () => 0))
  }, [externalValue])

  useEffect(() => {
    if (!autoFocusInput) {
      return
    }

    const firstInput = refs.current[0]

    if (firstInput) {
      firstInput.focus()
      firstInput.setSelectionRange(0, firstInput.value.length)
    }
  }, [autoFocusInput])

  return (
    <FormControl
      sx={[
        { mt: 6, width: '100%', alignItems: 'center' },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : [])
      ]}
      {...props}
    >
      {label && (
        <Typography fontWeight={500} sx={{ mb: 2, textAlign: 'center' }}>
          {label}
        </Typography>
      )}
      <Stack direction="row" spacing={2} justifyContent="center">
        {digits.map((digit) => (
          <TextField
            key={digit}
            value={value[digit]}
            inputMode="numeric"
            inputProps={{
              ref: (el: HTMLInputElement) => (refs.current[digit] = el),
              inputMode: 'numeric',
              pattern: '[0-9]*',
              maxLength: 1,
              min: 0,
              max: 9,
              sx: {
                maxWidth: '24px',
                fontSize: '24px !important',
                textAlign: 'center !important'
              },
              onKeyUp: (event) => {
                handleKeyUp(digit, event)
              },
              onPaste: (event) => {
                handlePaste(digit, event)
              }
            }}
            fullWidth
            onChange={(event) => {
              handleChange(digit, event.target.value)
            }}
          />
        ))}
      </Stack>
    </FormControl>
  )
}
